import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { ArrowUp, ArrowDown, BarChart3, TrendingUp } from "lucide-react";

const tooltipStyle = {
  contentStyle: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(11,37,69,0.08)",
    fontFamily: "Inter, sans-serif",
    fontSize: 13,
    color: "#0b2545",
  },
  labelStyle: { color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.1em" },
  cursor: { fill: "rgba(44,125,160,0.06)" },
};

export default function MarketDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/market-data")
      .then((res) => { if (mounted) { setData(res.data); setLoading(false); } })
      .catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const merged = data
    ? data.padova_trend.map((p, i) => ({
        m: p.m,
        padova: p.price,
        abano: data.abano_trend[i]?.price ?? null,
      }))
    : [];

  return (
    <section id="dashboard" className="relative py-24 lg:py-32 enea-paper-bg border-t border-slate-100" data-testid="dashboard-section">
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <div className="enea-eyebrow flex items-center gap-3 mb-5">
              <BarChart3 className="w-4 h-4 text-[#2c7da0]" />
              <span>03 · Dati Live · 2026</span>
            </div>
            <h2 className="text-3xl lg:text-4xl xl:text-[44px] font-display font-semibold leading-tight text-[#0b2545]">
              Mercato Immobiliare.
              <span className="block mt-3 text-slate-600 font-normal text-2xl lg:text-3xl">
                Tutta Italia. Aggiornamento continuo.
              </span>
            </h2>
          </div>
          <div className="text-xs uppercase tracking-[0.18em] font-medium text-slate-500 bg-white rounded-lg border border-slate-200 px-4 py-3 shadow-sm" data-testid="dashboard-clock">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#0d9488]" />
              <span className="text-[#0b2545]">Dati aggiornati · 2026</span>
            </div>
          </div>
        </div>

        {loading && (
          <div className="text-slate-500 text-sm" data-testid="dashboard-loading">Caricamento dataset…</div>
        )}

        {data && (
          <>
            {/* KPI ROW */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="dashboard-indices">
              {data.indices.map((idx, i) => (
                <div key={idx.label} className="bg-white rounded-xl border border-slate-200 p-6 lg:p-7 shadow-sm hover:shadow-md transition-shadow">
                  <div className="enea-eyebrow text-[10px]">IDX/{String(i + 1).padStart(2, "0")}</div>
                  <div className="mt-3 text-xs uppercase tracking-wider text-slate-500">{idx.label}</div>
                  <div className="mt-3 flex items-baseline gap-3">
                    <div className="text-3xl lg:text-[34px] font-display font-semibold text-[#0b2545] tracking-tight">{idx.value}</div>
                    <div className={`text-xs flex items-center gap-1 font-medium ${idx.trend === "up" ? "text-[#0d9488]" : "text-[#2c7da0]"}`}>
                      {idx.trend === "up" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                      {idx.delta}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CHARTS */}
            <div className="mt-8 grid grid-cols-12 gap-y-6 gap-x-2 md:gap-6">
              <div className="col-span-12 lg:col-span-8 bg-white rounded-xl border border-slate-200 p-6 lg:p-8 shadow-sm" data-testid="dashboard-price-chart">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="enea-eyebrow">Andamento · 01</div>
                    <div className="mt-1 text-lg font-display font-semibold text-[#0b2545]">Prezzo medio €/mq · serie mensile 2026</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-5 text-[12px]">
                    <span className="flex items-center gap-2 text-slate-700 font-medium"><span className="w-3 h-[3px] bg-[#0b2545]" />Capoluoghi</span>
                    <span className="flex items-center gap-2 text-slate-500"><span className="w-3 h-[3px] bg-[#2c7da0]" />Provincia</span>
                  </div>
                </div>
                <div className="h-72 min-h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={merged} margin={{ top: 10, right: 12, left: 0, bottom: 0 }}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="m" stroke="#94a3b8" tick={{ fontFamily: "Inter", fontSize: 11 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                      <YAxis stroke="#94a3b8" tick={{ fontFamily: "Inter", fontSize: 11 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} tickFormatter={(v) => `€${v}`} />
                      <Tooltip {...tooltipStyle} formatter={(v) => [`€${v}/mq`, ""]} />
                      <Line type="monotone" dataKey="padova" stroke="#0b2545" strokeWidth={2.5} dot={{ r: 3, fill: "#0b2545" }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="abano" stroke="#2c7da0" strokeWidth={2.5} dot={{ r: 3, fill: "#2c7da0" }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="col-span-12 lg:col-span-4 bg-white rounded-xl border border-slate-200 p-6 lg:p-8 shadow-sm" data-testid="dashboard-volume-chart">
                <div className="enea-eyebrow">Andamento · 02</div>
                <div className="mt-1 text-lg font-display font-semibold text-[#0b2545]">Volume transazioni · Italia</div>
                <div className="h-72 min-h-[280px] mt-5">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.padova_trend} margin={{ top: 10, right: 6, left: -16, bottom: 0 }}>
                      <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="m" stroke="#94a3b8" tick={{ fontFamily: "Inter", fontSize: 10 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                      <YAxis stroke="#94a3b8" tick={{ fontFamily: "Inter", fontSize: 10 }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                      <Tooltip {...tooltipStyle} formatter={(v) => [`${v} tx`, ""]} />
                      <Bar dataKey="volume" fill="#2c7da0" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* ZONE TABLE */}
            <div className="mt-8 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="dashboard-zones-table">
              <div className="p-6 lg:p-8 border-b border-slate-100 flex items-center justify-between">
                <div>
                  <div className="enea-eyebrow">Microzone · 03</div>
                  <div className="mt-1 text-lg font-display font-semibold text-[#0b2545]">€/mq e variazione annua</div>
                </div>
                <div className="flex items-center gap-2 text-[12px] text-slate-500">
                  <TrendingUp className="w-3.5 h-3.5 text-[#0d9488]" />
                  <span>fonte: rogiti, OMI, rilevazione diretta</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500 bg-slate-50/60">
                      <th className="text-left px-6 py-4 font-medium">Zona</th>
                      <th className="text-right px-6 py-4 font-medium">Prezzo €/mq</th>
                      <th className="text-right px-6 py-4 font-medium">Δ YoY</th>
                      <th className="text-right px-6 py-4 font-medium">Liquidità</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.zones.map((z) => (
                      <tr key={z.zone} className="border-b border-slate-100 last:border-b-0 hover:bg-slate-50/50 transition-colors">
                        <td className="px-6 py-4 text-slate-800 font-medium">{z.zone}</td>
                        <td className="px-6 py-4 text-right text-[#0b2545] font-semibold">€ {z.price_sqm.toLocaleString("it-IT")}</td>
                        <td className="px-6 py-4 text-right text-[#0d9488] font-semibold">+{z.delta_yoy.toFixed(1)}%</td>
                        <td className="px-6 py-4 text-right">
                          <span className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full font-medium ${z.liquidity === "Alta" ? "bg-[#0d9488]/10 text-[#0d9488]" : "bg-slate-100 text-slate-600"}`}>
                            {z.liquidity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-6 text-[12px] text-slate-500 flex items-center gap-2" data-testid="dashboard-disclaimer">
              <span className="text-[#2c7da0]">▪</span>
              Dataset dimostrativo. La versione operativa è alimentata da fonti notarili, OMI e rilevazione diretta. Calibrazione MCM continua.
            </div>
          </>
        )}
      </div>
    </section>
  );
}
